import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { accountsAPI, unwrapVendors } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';
import InlineIntegrityChip from '../components/InlineIntegrityChip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileDown, FileText, Receipt, Calculator, Building2,
  Download, Loader2, FileSpreadsheet, FileJson
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExportsPage = () => {
  const { selectedOrg } = useApp();
  const periodState = usePeriod(PERIOD_MODES.MONTH_SELECTION);
  // Every export uses the selected whole filing months (multi-month files carry a tax_period column).
  const months = periodState.months;
  const monthsParam = months.join(',');
  const multi = months.length > 1;
  const fileTag = multi ? `${months[0]}_to_${months[months.length - 1]}_${months.length}m` : months[0];
  const { canExport } = useAuth();
  const [loading, setLoading] = useState({});
  const [exportData, setExportData] = useState(null);
  const [selectedExport, setSelectedExport] = useState('');
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');

  const exportTypes = [
    { id: 'bills_register', name: 'Bills Register', icon: FileText, description: 'All bills and purchases', xlsxKey: 'bills_register' },
    { id: 'payments_register', name: 'Payments Register', icon: Receipt, description: 'All payments made', xlsxKey: 'payments_register' },
    { id: 'allocations_register', name: 'Allocations Register', icon: Receipt, description: 'Payment allocations', xlsxKey: 'allocations_register' },
    { id: 'input_tax', name: 'Input Tax Register', icon: Calculator, description: 'GST paid on purchases', xlsxKey: 'input_tax_register' },
    { id: 'output_tax', name: 'Output Tax Register', icon: Calculator, description: 'GST collected on sales', xlsxKey: 'output_tax_register' },
    { id: 'monthly_liability', name: 'Monthly Liability', icon: FileDown, description: 'Tax liability statement', xlsxKey: 'monthly_liability' },
    { id: 'gstr3b', name: 'GSTR-3B Summary', icon: FileDown, description: 'GSTR-3B summary (statutory monthly return)', singleMonthOnly: true },
    { id: 'vendor_ledger', name: 'Vendor Ledger', icon: Building2, description: 'Vendor transaction history', xlsxKey: 'vendor_ledger' },
  ];

  useEffect(() => {
    if (selectedOrg) {
      loadVendors();
    }
  }, [selectedOrg]);

  const loadVendors = async () => {
    try {
      const response = await accountsAPI.getVendors(selectedOrg?.id);
      setVendors(unwrapVendors(response.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      setVendors([]);
    }
  };

  const handleExportJSON = async (exportType) => {
    if (!selectedOrg) return;
    
    if (exportType === 'vendor_ledger' && !selectedVendor) {
      alert('Please select a vendor for ledger export');
      return;
    }

    setLoading(prev => ({ ...prev, [`${exportType}_json`]: true }));
    setSelectedExport(exportType);
    
    try {
      const exportRequest = {
        export_type: exportType === 'bills_register' ? 'purchase_register' : exportType,
        org_id: selectedOrg.id,
        months,
      };
      
      if (exportType === 'vendor_ledger') {
        exportRequest.vendor_id = selectedVendor;
      }
      

      const response = await accountsAPI.generateExport(exportRequest);
      setExportData(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
    
    setLoading(prev => ({ ...prev, [`${exportType}_json`]: false }));
  };

  const handleExportXLSX = async (exportType, xlsxKey) => {
    if (!xlsxKey) return;
    
    if (xlsxKey === 'vendor_ledger' && !selectedVendor) {
      alert('Please select a vendor for ledger export');
      return;
    }

    setLoading(prev => ({ ...prev, [`${exportType}_xlsx`]: true }));
    
    try {
      const token = sessionStorage.getItem('token');
      let url = `${BACKEND_URL}/api/accounts/exports/xlsx/${xlsxKey}`;
      
      const params = new URLSearchParams();
      if (xlsxKey === 'vendor_ledger' && selectedVendor) {
        params.append('vendor_id', selectedVendor);
      }
      params.append('months', monthsParam);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `${xlsxKey}_${fileTag}.xlsx`;
        
        // Try to get filename from Content-Disposition header
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]*)["']?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const body = await response.json().catch(() => ({}));
        toast.error(typeof body.detail === 'string' ? body.detail : (body.detail?.reason_human || 'Failed to download XLSX. Please try again.'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      alert('Failed to export XLSX. Please try again.');
    }
    
    setLoading(prev => ({ ...prev, [`${exportType}_xlsx`]: false }));
  };

  const downloadJSON = () => {
    if (!exportData) return;
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedExport}_${fileTag}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  if (!canExport()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don't have permission to access exports</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exports-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">CA Exports</h1>
          <p className="text-muted-foreground mt-1">
            Generate CA-friendly reports in JSON or XLSX format
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <InlineIntegrityChip surface="exports" />
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.MONTH_SELECTION} />
        </div>
      </div>

      {/* Vendor Selector for Ledger Export */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Building2 className="w-5 h-5 text-muted-foreground" />
            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
              <SelectTrigger className="w-[300px] bg-background border-border/50">
                <SelectValue placeholder="Select vendor (for ledger export)" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map(v => (
                  <SelectItem key={v.vendor_id || v.id} value={v.vendor_id || v.id}>
                    {v.name || v.vendor_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">Required for Vendor Ledger export</span>
          </div>
        </CardContent>
      </Card>

      {/* Export Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {exportTypes.map((exp) => (
          <Card 
            key={exp.id} 
            className="bg-card/50 backdrop-blur-sm border-border/50"
            data-testid={`export-${exp.id}`}
          >
            <CardContent className="p-5">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="p-2 rounded-lg bg-emerald-500/10 w-fit">
                    <exp.icon className="w-5 h-5 text-emerald-400" />
                  </div>
                </div>
                <div>
                  <h3 className="font-manrope font-medium">{exp.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{exp.description}</p>
                  {exp.singleMonthOnly && multi && (
                    <p className="text-[11px] text-amber-300 mt-1" data-testid={`export-${exp.id}-single-month-only`}>Statutory return — select exactly one month</p>
                  )}
                </div>
                
                {/* Export Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-border/50 hover:bg-blue-500/10 hover:border-blue-500/50"
                    onClick={() => handleExportJSON(exp.id)}
                    disabled={loading[`${exp.id}_json`] || (exp.singleMonthOnly && multi)}
                    data-testid={`export-${exp.id}-json`}
                  >
                    {loading[`${exp.id}_json`] ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <FileJson className="w-3 h-3 mr-1" />
                    )}
                    JSON
                  </Button>
                  
                  {exp.xlsxKey && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-border/50 hover:bg-emerald-500/10 hover:border-emerald-500/50"
                      onClick={() => handleExportXLSX(exp.id, exp.xlsxKey)}
                      disabled={loading[`${exp.id}_xlsx`]}
                      data-testid={`export-${exp.id}-xlsx`}
                    >
                      {loading[`${exp.id}_xlsx`] ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="w-3 h-3 mr-1" />
                      )}
                      XLSX
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Export Result */}
      {exportData && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-manrope">Export Result</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {exportData.export_type} - {exportData.record_count} records
              </p>
            </div>
            <Button onClick={downloadJSON} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-btn">
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </CardHeader>
          <CardContent>
            <div className="bg-background/50 rounded-lg p-4 max-h-[400px] overflow-auto">
              <pre className="text-xs font-mono text-muted-foreground">
                {JSON.stringify(exportData.data, null, 2).slice(0, 5000)}
                {JSON.stringify(exportData.data, null, 2).length > 5000 && '\n... (truncated)'}
              </pre>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Generated at: {new Date(exportData.generated_at).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExportsPage;
